import { Divider, Transfer } from 'antd';
import { TransferDirection } from 'antd/lib/transfer';
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import ActionArea from '../../components/ActionArea'
import Button from '../../components/UI/Button';
import SelectInput from '../../components/UI/SelectInput'
import { useLoading } from '../../context/LoadingProvider/useLoading';
import { getMigrationQueueItems, saveMigrationQueueItems } from '../../services/migrationQueue';
import { notificationError } from '../../services/notification';
import { sendSystemLog, useAuth } from '../../context/AuthProvider/useAuth';

export interface IDistributorMigration {
  id: number;
  name: string;
  servers: IServerMigration[]
}

interface IServerMigration {
  id: number;
  alias: string;
  locations: ILocationMigration[]
}

interface ILocationMigration {
  id: number;
  name: string;
}


function index() {
  const loading = useLoading()
  const history = useNavigate()
  const {canEdit} = useAuth()
  const [items, setItems] = useState<IDistributorMigration[]>([])
  const [distributor, setDistributor] = useState<number>()
  const [server, setServer] = useState<{source: number | null, target: number | null}>({
    source: null,
    target: null
  })
  const [targetKeys, setTargetKeys] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [selectedInfo, setSelectedInfo] = useState<{
    id: string,
    name: string,
    sourceServerId: number,
    targetServerId: number,
    targetServerName: string
  }[]>([]);
  const log = {
    log_type: 'visited',
    log_object: 'migration_queue'
  }

  useEffect(() => {
    sendSystemLog(log);
  }, [])

  useEffect(() => {
    (async () => {
      loading.addLoading()
      const res = await getMigrationQueueItems()
      if (res.length === 0) return
      setItems(res)
      setDistributor(res[0].id)
      setServer({
        source: res[0].servers[0].id,
        target: res[0].servers[0].id
      })
      loading.removeLoading()
    })()
  }, [])

  useEffect(() => {
    if(!distributor && items.length === 0) return
    const selectedDistributor = items.find(item => distributor === item.id)
    if(!selectedDistributor) return
    setServer({
      source: selectedDistributor.servers[0].id,
      target: selectedDistributor.servers[0].id
    })
  },[distributor])

  const onChange = (nextTargetKeys: string[], direction: TransferDirection, moveKeys: string[]) => {
    if(server.source === server.target && direction === 'right') return notificationError('The TARGET server cant be the same as the SOURCE server')
    const newSelectedInfo: typeof selectedInfo = []
    const selectedInfoCopy = [...selectedInfo]

    nextTargetKeys.forEach((key, i) => {
      if(targetKeys.find((target) => target === key) ) return 
      const target = items.find((item) => item.id === distributor)?.servers.find(distributorServer => distributorServer.id === server.target)
      const locationName = items.find((item) => item.id === distributor)?.servers.find(distributorServer => distributorServer.id === server.source)?.locations.find((item) => item.id.toString() === key)?.name
      if(!server.source || !server.target || !locationName || !target?.alias) return 
      newSelectedInfo.push({
        id: key,
        name: locationName,
        sourceServerId: server.source,
        targetServerId: server.target,
        targetServerName: target?.alias
      })
    })

    selectedInfoCopy.slice().reverse().forEach((selected, i) => {
      if (nextTargetKeys.find((key) => key === selected.id)) return;
      selectedInfoCopy.splice(selectedInfoCopy.length - 1 - i, 1);
    });


    setSelectedInfo([...newSelectedInfo, ...selectedInfoCopy])
    setTargetKeys(nextTargetKeys);
  };

  const onSelectChange = (sourceSelectedKeys: string[], targetSelectedKeys: string[]) => {
    setSelectedKeys([...sourceSelectedKeys, ...targetSelectedKeys]);
  };

  const distributorOptions = items.map((item) => {
    return { name: item.name, value: item.id }
  })

  const renderTransferRow = (row: {key: string, title: string}) => {
    const select = selectedInfo.find((selected) => row.key === selected.id)
    if(!select) return row.title
    return `${row.title} => ${select.targetServerName}`
  }

  const submit = async () => {
    loading.addLoading()
    const valid = await saveMigrationQueueItems(selectedInfo)
    const res = await getMigrationQueueItems()
    if(valid) setSelectedInfo([])
    if (res.length !== 0) setItems(res)
    loading.removeLoading()
  }

  const serverOptions = items.find((item) => item.id === distributor)?.servers.map((server) => {return {name: server.alias, value: server.id}}) || [{name: '', value: ''}]
  let selectableLocations = items.find((item) => item.id === distributor)?.servers.find(distributorServer => server.source === distributorServer.id)?.locations.map((location) => {return {key: location.id.toString(), title: `${location.name}`}}) || []
  selectableLocations = [...selectableLocations, ...selectedInfo.map((item) => {return {key: item.id.toString(), title: item.name}})]
  return (
    <div className="migration-queue">
      {distributorOptions.length > 0 && (
        <ActionArea buttonAction={() => history('/migrationstatus')} buttonText='Go to migration Status'>
          <>
            <SelectInput
              label="Distributor"
              options={distributorOptions}
              value={distributor}
              onSelectChange={(e) => setDistributor(e)}
            />
            <SelectInput
              label="SOURCE server"
              options={serverOptions}
              value={server.source}
              onSelectChange={(e) =>setServer({...server, source: e})}
            />
            <SelectInput
              label="TARGET server"
              options={serverOptions}
              value={server.target}
              onSelectChange={(e) => setServer({...server, target: e})}
            />
          </>
        </ActionArea>
      )}
      <div style={{width: '100%', margin: 'auto', display: 'flex'}}>
        <Transfer
          dataSource={selectableLocations}
          titles={['Locations in SOURCE server ', 'Selected locations and TARGET servers']}
          targetKeys={targetKeys}
          style={{margin: 'auto'}}
          listStyle={{width: window.innerWidth > 1100 ? '44vw' : '48vw',}}
          selectedKeys={selectedKeys}
          onChange={onChange}
          onSelectChange={onSelectChange}
          render={(item) => renderTransferRow(item)}
          disabled={!canEdit}
        />
      </div>
      <div className="migration-queue__save">
        <Divider />
        <Button disabled={selectedInfo.length === 0} type="primary" onClick={submit}> 
          <p id="save-button">Add selected to migration queue</p>
        </Button>
      </div>
    </div>
  )
}

export default index